import { createFileRoute, redirect } from '@tanstack/react-router';

import { AppShell } from '@/app/shell';
import { useSesion, useSesionStore } from '@/entities/usuario';
import { PrivateSessionGuard } from '@/processes/auth';
import { Skeleton } from '@/shared/ui';

/**
 * Layout privado.
 * - beforeLoad espera a que termine la hidratacion y redirige a /login
 *   si no hay sesion autenticada.
 * - PrivateSessionGuard inicia los procesos de inactividad y broadcast
 *   logout, exponiendo overlays (modal de aviso) al AppShell.
 */
export const Route = createFileRoute('/_private')({
  beforeLoad: async ({ location }) => {
    await waitForHydration();
    const state = useSesionStore.getState();
    if (!state.isAuthenticated) {
      // eslint-disable-next-line @typescript-eslint/only-throw-error -- TanStack Router redirect pattern
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: PrivateLayout,
});

function PrivateLayout() {
  const { user, isHydrated } = useSesion();

  if (!isHydrated || !user) return <ProtectedSkeleton />;

  return (
    <PrivateSessionGuard>{(overlays) => <AppShell overlays={overlays} />}</PrivateSessionGuard>
  );
}

function ProtectedSkeleton() {
  return (
    <div className="flex min-h-screen flex-col gap-4 p-6">
      <Skeleton className="h-8 w-48" />
      <Skeleton className="h-32 w-full" />
      <Skeleton className="h-32 w-full" />
    </div>
  );
}

function waitForHydration(): Promise<void> {
  if (useSesionStore.getState().isHydrated) return Promise.resolve();
  return new Promise<void>((resolve) => {
    const unsubscribe = useSesionStore.subscribe((s) => {
      if (s.isHydrated) {
        unsubscribe();
        resolve();
      }
    });
  });
}
